/* Preferences belong to the selected profile, including late network replies. */
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else Object.defineProperty(root, "LouderrPrivacy", {value: Object.freeze(api)});
})(typeof window === "undefined" ? null : window, function () {
  "use strict";
  const policies = new Set(["everyone", "following", "nobody"]);
  function create({root, api, getIdentity, onChange = () => {}}) {
    const select = root.querySelector("select");
    const receipts = root.querySelector("[data-read-receipts]");
    const typing = root.querySelector("[data-typing-indicators]");
    const status = root.querySelector("[role=status]");
    const error = root.querySelector("[role=alert]");
    const retry = root.querySelector("[data-privacy-retry]");
    let identity = null, revision = 0, value = null, busy = null;
    const same = (a, b) => a?.viewerId === b?.viewerId && a?.csrf === b?.csrf && a?.social === b?.social;
    function render(message = "", failure = "") {
      select.value = value?.message_policy || "";
      select.disabled = !identity?.viewerId || !value || !!busy;
      receipts.checked = value?.read_receipts_enabled === true;
      receipts.disabled = select.disabled;
      if (typing) { typing.checked = value?.typing_indicators_enabled === true; typing.disabled = select.disabled; }
      root.setAttribute("aria-busy", busy ? "true" : "false");
      status.textContent = message;
      error.textContent = failure;
      error.hidden = !failure;
      retry.hidden = !failure;
      retry.disabled = !!busy;
      onChange();
    }
    function syncIdentity() {
      const current = getIdentity();
      if (same(identity, current)) return false;
      identity = current; revision++; value = null; busy = null;
      render();
      return true;
    }
    function begin(kind) {
      const operation = {identity, revision: ++revision};
      busy = kind;
      render(kind === "save" ? "Saving…" : "Loading…");
      return operation;
    }
    const live = op => op.revision === revision && same(op.identity, getIdentity());
    function confirmed(payload, op) {
      if (payload?.user_id !== op.identity.viewerId || !policies.has(payload?.message_policy)
          || typeof payload?.read_receipts_enabled !== "boolean"
          || typeof payload?.typing_indicators_enabled !== "boolean"
          || !Number.isSafeInteger(payload?.typing_preference_version) || payload.typing_preference_version < 0
          || (payload.typing_indicators_enabled && payload.typing_preference_version < 1)) {
        throw new Error("Invalid privacy response");
      }
      return {message_policy: payload.message_policy, read_receipts_enabled: payload.read_receipts_enabled,
        typing_indicators_enabled: payload.typing_indicators_enabled, typing_preference_version: payload.typing_preference_version};
    }
    async function refresh() {
      syncIdentity();
      if (!identity?.viewerId || busy === "save") return;
      const op = begin("load");
      try {
        const result = await api("/api/account/privacy");
        if (!live(op)) return;
        value = confirmed(result, op); busy = null; render();
      } catch (_) {
        if (!live(op)) return;
        value = null; busy = null;
        render("", "Couldn’t load your privacy settings. Try again.");
      }
    }
    async function save(field, requested) {
      if (syncIdentity()) { await refresh(); return; }
      if (busy || !value || (field === "message_policy" && !policies.has(requested))) { render(); return; }
      if (requested === value[field]) return;
      const op = begin("save");
      try {
        const result = await api("/api/account/privacy", {method: "PATCH", csrf: true, json: {[field]: requested}});
        if (!live(op)) return;
        value = confirmed(result, op);
        if (value[field] !== requested) throw new Error("Preference did not match");
        busy = null; render("Saved");
      } catch (_) {
        if (!live(op)) return;
        // A lost response may still have committed. Read the actual value before
        // claiming either success or failure; never restore an assumed old value.
        try {
          const result = await api("/api/account/privacy");
          if (!live(op)) return;
          value = confirmed(result, op); busy = null;
          render(value[field] === requested ? "Saved" : "", value[field] === requested ? "" : "Couldn’t save that change. Your current setting is shown.");
        } catch (_) {
          if (!live(op)) return;
          value = null; busy = null;
          render("", "Couldn’t confirm your setting. Try again to check it.");
        }
      }
    }
    select.addEventListener("change", () => save("message_policy", select.value));
    receipts.addEventListener("change", () => save("read_receipts_enabled", receipts.checked));
    typing?.addEventListener("change", () => save("typing_indicators_enabled", typing.checked));
    retry.addEventListener("click", refresh);
    syncIdentity();
    render();
    // Unknown or in-flight consent never licenses the publication of a read.
    // The tracker still acknowledges it privately so unread state can advance.
    const readReceiptsEnabled = () => same(identity, getIdentity()) && !busy
      && value?.read_receipts_enabled === true;
    const typingConsent = () => same(identity, getIdentity()) && !busy && value
      ? {enabled: value.typing_indicators_enabled, version: value.typing_preference_version} : null;
    return Object.freeze({refresh, syncIdentity, readReceiptsEnabled, typingConsent});
  }
  return {create};
});
